import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import ProductCard from '../components/ProductCard';
import QuickView from '../components/QuickView';
import { Product, PRODUCT_SELECT } from '../types/product';

export default function ProductsPage() {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [quickViewProduct, setQuickViewProduct] = useState<Product | null>(null);
  const [isQuickViewOpen, setIsQuickViewOpen] = useState(false);

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const searchQuery = searchParams.get('search') || '';
  const categoryParam = searchParams.get('category') || '';

  useDocumentMeta(
    searchQuery ? `Risultati per "${searchQuery}"` : categoryParam ? categoryParam : 'Prodotti',
    'Scopri tutti i prodotti artigianali di Apicoltura DePiccoli: miele, propoli e prodotti dell\'alveare.'
  );

  useEffect(() => {
    if (categoryParam && categoryParam !== selectedCategory) {
      setSelectedCategory(categoryParam);
    }
    // Only re-sync when the URL param changes, not on every selectedCategory update
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryParam]);

  const fetchProducts = async () => {
    setLoading(true);

    // Build the query
    let query = supabase.from('products').select(PRODUCT_SELECT).eq('active', true);

    // Apply category filter (set via links from the Categories page)
    if (selectedCategory) {
      query = query.eq('category', selectedCategory);
    }

    // Apply search filter
    if (searchQuery) {
      query = query.ilike('name', `%${searchQuery}%`);
    }

    const { data, error } = await query;

    if (error) {
      console.error(error);
    } else {
      setProducts(data || []);
    }

    setLoading(false);
  };

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      fetchProducts();
    }, 300); // Add a small delay to prevent rapid re-fetching

    return () => clearTimeout(debounceTimer);
    // fetchProducts isn't memoized; omitted to avoid refetching on every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory, searchQuery]);

  return (
    <div className="container mx-auto py-10">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-foreground">
          {searchQuery ? `Risultati per "${searchQuery}"` : selectedCategory ? `${selectedCategory}` : 'Prodotti'}
        </h1>
      </div>

      {/* Products Grid */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      ) : products.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
          {products.map((product) => (
            <div key={product.id} className="transform-gpu">
              <ProductCard
                product={product}
                onQuickView={(product) => {
                  setQuickViewProduct(product);
                  setIsQuickViewOpen(true);
                }}
              />
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-10">
          <p className="text-muted-foreground">Nessun prodotto trovato.</p>
        </div>
      )}
      
      {/* QuickView Modal */}
      {isQuickViewOpen && quickViewProduct && (
        <QuickView
          product={quickViewProduct}
          onClose={() => setIsQuickViewOpen(false)}
        />
      )}
    </div>
  );
}
